import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import type { QualityProfile } from "../../app/performance";

export type DiceOutcome = "win" | "draw" | "lose";

export type DiceRollResult = {
  player: number;
  rival: number;
  outcome: DiceOutcome;
};

type DieState = {
  root: THREE.Group;
  home: THREE.Vector3;
  velocity: THREE.Vector3;
  angularVelocity: THREE.Vector3;
  settleFromPosition: THREE.Vector3;
  settleFromQuaternion: THREE.Quaternion;
  settleQuaternion: THREE.Quaternion;
  shadow: THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>;
  value: number;
};

const FACE_NORMALS: Record<number, THREE.Vector3> = {
  1: new THREE.Vector3(0, 1, 0),
  2: new THREE.Vector3(0, 0, 1),
  3: new THREE.Vector3(1, 0, 0),
  4: new THREE.Vector3(-1, 0, 0),
  5: new THREE.Vector3(0, 0, -1),
  6: new THREE.Vector3(0, -1, 0),
};

const PIPS: Record<number, Array<[number, number]>> = {
  1: [[0, 0]],
  2: [[-1, 1], [1, -1]],
  3: [[-1, 1], [0, 0], [1, -1]],
  4: [[-1, 1], [1, 1], [-1, -1], [1, -1]],
  5: [[-1, 1], [1, 1], [0, 0], [-1, -1], [1, -1]],
  6: [[-1, 1], [-1, 0], [-1, -1], [1, 1], [1, 0], [1, -1]],
};

export class DiceDuel {
  readonly group = new THREE.Group();

  private readonly playerDie: DieState;
  private readonly rivalDie: DieState;
  private readonly floorGlow: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  private active = false;
  private rolling = false;
  private rollElapsed = 0;
  private settleStarted = false;
  private resultSent = false;

  constructor(
    quality: QualityProfile,
    private readonly onResult: (result: DiceRollResult) => void,
  ) {
    this.group.visible = false;
    this.group.add(this.buildTable(quality), this.buildDiceLights());

    this.playerDie = this.createDie(0xf7f8ff, 0x11131b, -0.82);
    this.rivalDie = this.createDie(0x202b42, 0xf2f5ff, 0.82);

    this.group.add(this.playerDie.root, this.rivalDie.root);

    this.floorGlow = new THREE.Mesh(
      new THREE.RingGeometry(0.72, 1.35, 64),
      new THREE.MeshBasicMaterial({
        color: 0x8290ff,
        transparent: true,
        opacity: 0.12,
        depthWrite: false,
      }),
    );
    this.floorGlow.rotation.x = -Math.PI / 2;
    this.floorGlow.position.y = 0.014;
    this.group.add(this.floorGlow);

    this.reset();
  }

  setActive(value: boolean): void {
    this.active = value;
    this.group.visible = value;
    if (value) this.reset();
  }

  reset(): void {
    this.rolling = false;
    this.rollElapsed = 0;
    this.settleStarted = false;
    this.resultSent = false;
    this.resetDie(this.playerDie);
    this.resetDie(this.rivalDie);
  }

  isRolling(): boolean {
    return this.rolling;
  }

  roll(): boolean {
    if (!this.active || this.rolling) return false;

    this.rolling = true;
    this.rollElapsed = 0;
    this.settleStarted = false;
    this.resultSent = false;

    this.launchDie(this.playerDie, -1);
    this.launchDie(this.rivalDie, 1);

    return true;
  }

  update(delta: number, elapsed: number): void {
    if (!this.active) return;

    this.floorGlow.material.opacity = 0.1 + (Math.sin(elapsed * 2.1) + 1) * 0.025;

    if (!this.rolling) {
      const idle = Math.sin(elapsed * 1.6) * 0.015;
      this.playerDie.root.position.y = this.playerDie.home.y + idle;
      this.rivalDie.root.position.y = this.rivalDie.home.y - idle;
      return;
    }

    this.rollElapsed += delta;

    const physicsTime = 0.9;
    const settleTime = 0.34;

    if (this.rollElapsed < physicsTime) {
      this.physicsStep(this.playerDie, delta);
      this.physicsStep(this.rivalDie, delta);
      return;
    }

    if (!this.settleStarted) {
      this.settleStarted = true;
      this.captureSettle(this.playerDie);
      this.captureSettle(this.rivalDie);
    }

    const t = THREE.MathUtils.clamp(
      (this.rollElapsed - physicsTime) / settleTime,
      0,
      1,
    );
    const eased = 1 - Math.pow(1 - t, 3);

    this.settleDie(this.playerDie, eased);
    this.settleDie(this.rivalDie, eased);

    if (t >= 1 && !this.resultSent) {
      this.resultSent = true;
      this.rolling = false;

      const player = this.playerDie.value;
      const rival = this.rivalDie.value;
      const outcome: DiceOutcome =
        player > rival ? "win" : player < rival ? "lose" : "draw";

      this.onResult({ player, rival, outcome });
    }
  }

  private createDie(
    bodyColor: number,
    pipColor: number,
    x: number,
  ): DieState {
    const root = new THREE.Group();

    const body = new THREE.Mesh(
      new RoundedBoxGeometry(0.94, 0.94, 0.94, 8, 0.145),
      new THREE.MeshPhysicalMaterial({
        color: bodyColor,
        roughness: 0.18,
        metalness: 0.06,
        clearcoat: 0.95,
        clearcoatRoughness: 0.12,
        reflectivity: 0.75,
      }),
    );
    body.castShadow = true;
    body.receiveShadow = true;
    root.add(body);

    const pipMaterial = new THREE.MeshPhysicalMaterial({
      color: pipColor,
      roughness: 0.24,
      metalness: 0.08,
      clearcoat: 0.48,
      clearcoatRoughness: 0.2,
    });
    const pipGeometry = new THREE.SphereGeometry(0.06, 16, 12);

    this.addFacePips(root, pipGeometry, pipMaterial, 1, new THREE.Vector3(0, 1, 0));
    this.addFacePips(root, pipGeometry, pipMaterial, 6, new THREE.Vector3(0, -1, 0));
    this.addFacePips(root, pipGeometry, pipMaterial, 2, new THREE.Vector3(0, 0, 1));
    this.addFacePips(root, pipGeometry, pipMaterial, 5, new THREE.Vector3(0, 0, -1));
    this.addFacePips(root, pipGeometry, pipMaterial, 3, new THREE.Vector3(1, 0, 0));
    this.addFacePips(root, pipGeometry, pipMaterial, 4, new THREE.Vector3(-1, 0, 0));

    const shadow = new THREE.Mesh(
      new THREE.CircleGeometry(0.58, 40),
      new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 0.28,
        depthWrite: false,
      }),
    );
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.set(x, 0.052, 0);
    this.group.add(shadow);

    const home = new THREE.Vector3(x, 0.57, 0);

    return {
      root,
      home,
      velocity: new THREE.Vector3(),
      angularVelocity: new THREE.Vector3(),
      settleFromPosition: new THREE.Vector3(),
      settleFromQuaternion: new THREE.Quaternion(),
      settleQuaternion: new THREE.Quaternion(),
      shadow,
      value: 1,
    };
  }

  private addFacePips(
    root: THREE.Group,
    geometry: THREE.SphereGeometry,
    material: THREE.MeshStandardMaterial,
    value: number,
    normal: THREE.Vector3,
  ): void {
    const up =
      Math.abs(normal.y) > 0.9
        ? new THREE.Vector3(0, 0, -1)
        : new THREE.Vector3(0, 1, 0);
    const right = new THREE.Vector3().crossVectors(up, normal).normalize();
    const faceUp = new THREE.Vector3().crossVectors(normal, right).normalize();

    for (const [px, py] of PIPS[value]) {
      const pip = new THREE.Mesh(geometry, material);
      pip.position
        .copy(normal)
        .multiplyScalar(0.478)
        .addScaledVector(right, px * 0.22)
        .addScaledVector(faceUp, py * 0.22);
      pip.scale.set(1, 0.42, 1);
      pip.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal);
      root.add(pip);
    }
  }

  private launchDie(die: DieState, side: -1 | 1): void {
    die.value = 1 + Math.floor(Math.random() * 6);
    die.root.position.set(side * 0.78, 1.82 + Math.random() * 0.34, 0.45);
    die.root.quaternion.setFromEuler(
      new THREE.Euler(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI,
      ),
    );

    die.velocity.set(
      side * (0.35 + Math.random() * 0.28),
      2.2 + Math.random() * 0.45,
      -1.7 - Math.random() * 0.6,
    );

    die.angularVelocity.set(
      (4.4 + Math.random() * 3.4) * (Math.random() > 0.5 ? 1 : -1),
      (5.2 + Math.random() * 3.7) * (Math.random() > 0.5 ? 1 : -1),
      (4.8 + Math.random() * 3.2) * (Math.random() > 0.5 ? 1 : -1),
    );
  }

  private physicsStep(die: DieState, delta: number): void {
    die.velocity.y -= 7.8 * delta;
    die.root.position.addScaledVector(die.velocity, delta);

    const angularSpeed = die.angularVelocity.length();
    if (angularSpeed > 0.001) {
      const axis = die.angularVelocity.clone().normalize();
      const deltaQ = new THREE.Quaternion().setFromAxisAngle(
        axis,
        angularSpeed * delta,
      );
      die.root.quaternion.premultiply(deltaQ).normalize();
    }

    die.angularVelocity.multiplyScalar(Math.pow(0.986, delta * 60));

    const floorY = 0.53;
    if (die.root.position.y < floorY) {
      die.root.position.y = floorY;
      if (die.velocity.y < 0) {
        die.velocity.y = -die.velocity.y * 0.42;
        die.velocity.x *= 0.84;
        die.velocity.z *= 0.84;
        die.angularVelocity.multiplyScalar(0.82);
      }
    }

    die.root.position.x = THREE.MathUtils.clamp(die.root.position.x, -1.75, 1.75);
    die.root.position.z = THREE.MathUtils.clamp(die.root.position.z, -1.2, 1.0);
    this.updateContactShadow(die);
  }

  private captureSettle(die: DieState): void {
    die.settleFromPosition.copy(die.root.position);
    die.settleFromQuaternion.copy(die.root.quaternion);

    const normal = FACE_NORMALS[die.value].clone().normalize();
    const base = new THREE.Quaternion().setFromUnitVectors(
      normal,
      new THREE.Vector3(0, 1, 0),
    );
    const yaw = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0),
      (Math.random() - 0.5) * 0.6,
    );
    die.settleQuaternion.multiplyQuaternions(yaw, base).normalize();
  }

  private settleDie(die: DieState, t: number): void {
    die.root.position.lerpVectors(
      die.settleFromPosition,
      die.home,
      t,
    );
    die.root.quaternion.slerpQuaternions(
      die.settleFromQuaternion,
      die.settleQuaternion,
      t,
    );
    this.updateContactShadow(die);
  }

  private resetDie(die: DieState): void {
    die.value = 1;
    die.root.position.copy(die.home);
    die.root.quaternion.identity();
    die.velocity.set(0, 0, 0);
    die.angularVelocity.set(0, 0, 0);
    this.updateContactShadow(die);
  }

  private updateContactShadow(die: DieState): void {
    const height = Math.max(0, die.root.position.y - 0.53);
    const spread = 1 + Math.min(height * 0.34, 0.48);
    const opacity = THREE.MathUtils.clamp(0.34 - height * 0.11, 0.08, 0.34);

    die.shadow.position.x = die.root.position.x;
    die.shadow.position.z = die.root.position.z;
    die.shadow.scale.setScalar(spread);
    die.shadow.material.opacity = opacity;
  }

  private buildDiceLights(): THREE.Group {
    const lights = new THREE.Group();

    const key = new THREE.PointLight(0xffffff, 24, 8, 2);
    key.position.set(-2.2, 4.4, 3.4);
    lights.add(key);

    const rim = new THREE.PointLight(0x7f91ff, 18, 7, 2);
    rim.position.set(2.8, 2.8, -2.8);
    lights.add(rim);

    const warm = new THREE.PointLight(0xffd9ad, 10, 5.5, 2);
    warm.position.set(0.2, 1.8, 3.2);
    lights.add(warm);

    return lights;
  }

  private buildTable(quality: QualityProfile): THREE.Group {
    const table = new THREE.Group();

    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(2.35, 2.5, 0.34, 64),
      new THREE.MeshStandardMaterial({
        color: 0x111827,
        roughness: 0.5,
        metalness: 0.38,
      }),
    );
    base.position.y = -0.18;
    base.receiveShadow = quality.shadows;
    table.add(base);

    const felt = new THREE.Mesh(
      new THREE.CylinderGeometry(2.12, 2.12, 0.06, 64),
      new THREE.MeshStandardMaterial({
        color: 0x232d49,
        roughness: 0.78,
        metalness: 0.04,
      }),
    );
    felt.position.y = 0.02;
    felt.receiveShadow = quality.shadows;
    table.add(felt);

    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(2.16, 0.055, 12, 80),
      new THREE.MeshStandardMaterial({
        color: 0x8d9cff,
        emissive: 0x4454c0,
        emissiveIntensity: 0.42,
        roughness: 0.24,
        metalness: 0.52,
      }),
    );
    rim.rotation.x = Math.PI / 2;
    rim.position.y = 0.065;
    table.add(rim);

    return table;
  }
}
